import { isNil, isObject, get, set, unset, sortBy } from 'lodash';

export const MAX_SERIALIZED_SIZE = 5000;
export const MAX_STRING_LENGTH = 200;
const PROGRESSIVE_ARRAY_LIMITS = [25, 10, 1];
const MAX_TRUNCATED_FIELDS = 3;

const buildTruncationMap = obj => {
    const arrayMap = new Map();
    const stringMap = new Map();

    const scanRecursive = (value, pathArray = []) => {
        if (isNil(value)) {
            return;
        }

        if (Array.isArray(value) && value.length > 0) {
            arrayMap.set(pathArray, value.length);
            value.forEach((item, index) => {
                scanRecursive(item, [...pathArray, index.toString()]);
            });
        } else if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
            stringMap.set(pathArray, value.length);
        } else if (isObject(value)) {
            for (const [key, val] of Object.entries(value)) {
                const currentPath = [...pathArray, key];
                scanRecursive(val, currentPath);
            }
        }
    };

    scanRecursive(obj);
    return { arrayMap, stringMap };
};

const sortPaths = pathsMap => {
    return sortBy(Array.from(pathsMap.keys()), [path => -path.length, path => -pathsMap.get(path)]);
};

const truncateArrayAtPath = (obj, pathArray, maxLength) => {
    const currentArray = get(obj, pathArray);

    if (Array.isArray(currentArray) && currentArray.length > maxLength) {
        set(obj, pathArray, currentArray.slice(0, maxLength));
        return true;
    }

    return false;
};

const truncateStringAtPath = (obj, pathArray) => {
    const currentString = get(obj, pathArray);

    if (typeof currentString === 'string' && currentString.length > MAX_STRING_LENGTH) {
        set(obj, pathArray, currentString.substring(0, MAX_STRING_LENGTH));
        return true;
    }

    return false;
};

const pathArrayToString = pathArray => {
    return pathArray
        .map(segment =>
            /^\d+$/.test(segment) ? `[${segment}]` : pathArray.indexOf(segment) === 0 ? segment : `.${segment}`
        )
        .join('');
};

const createObjectWithTruncationMetadata = (workingObj, truncatedFieldsSet) => {
    return {
        ...workingObj,
        truncated: true,
        truncatedFields: Array.from(truncatedFieldsSet)
            .slice(-MAX_TRUNCATED_FIELDS)
            .map(field =>
                field.length > MAX_STRING_LENGTH ? `${field.substring(0, MAX_STRING_LENGTH - 3)}...` : field
            ),
    };
};

const removeObjectProperties = (workingObj, targetSize, truncatedFieldsSet) => {
    const propertyMap = new Map();

    const scanRecursive = (obj, pathArray = []) => {
        if (isNil(obj)) return;

        if (isObject(obj) && !Array.isArray(obj)) {
            for (const [key, value] of Object.entries(obj)) {
                const currentPath = [...pathArray, key];

                const size = JSON.stringify(value, nullReplacer).length;

                propertyMap.set(currentPath, size);

                if (isObject(value) && !Array.isArray(value)) {
                    scanRecursive(value, currentPath);
                }
            }
        }
    };

    scanRecursive(workingObj);

    const sortedPaths = Array.from(propertyMap.entries()).sort(([_, sizeA], [__, sizeB]) => {
        return sizeA - sizeB;
    });

    const currentSize = JSON.stringify(
        createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet),
        nullReplacer
    ).length;

    // First pass: Look for optimal single removal
    for (const [pathArray, propertySize] of sortedPaths) {
        if (get(workingObj, pathArray) !== undefined) {
            if (currentSize - propertySize <= targetSize) {
                unset(workingObj, pathArray);
                truncatedFieldsSet.add(pathArrayToString(pathArray));

                const newSize = JSON.stringify(
                    createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet),
                    nullReplacer
                ).length;

                if (newSize <= targetSize) {
                    return true;
                }
                // If we're still over, continue to second pass
                break;
            }
        }
    }

    // Second pass: Iterative removal if no single property suffices
    for (const [pathArray] of sortedPaths) {
        if (get(workingObj, pathArray) !== undefined) {
            unset(workingObj, pathArray);
            truncatedFieldsSet.add(pathArrayToString(pathArray));

            const newSize = JSON.stringify(
                createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet),
                nullReplacer
            ).length;

            if (newSize <= targetSize) {
                return true;
            }
        }
    }
    return false;
};

const nullReplacer = (key, val) => {
    if (!isNil(val)) return val;
};

const serializedField = value => {
    if (isNil(value) || typeof value === 'string') {
        return value;
    }

    try {
        const initialSerialization = JSON.stringify(value, nullReplacer);

        if (initialSerialization.length <= MAX_SERIALIZED_SIZE) {
            return initialSerialization;
        }

        let workingObj = JSON.parse(initialSerialization);
        const truncatedFieldsSet = new Set();

        const { arrayMap, stringMap } = buildTruncationMap(workingObj);
        const sortedArrayPaths = sortPaths(arrayMap);
        const sortedStringPaths = sortPaths(stringMap);

        // First, try truncating long strings
        for (const pathArray of sortedStringPaths) {
            const didTruncate = truncateStringAtPath(workingObj, pathArray);

            if (didTruncate) {
                truncatedFieldsSet.add(pathArrayToString(pathArray));

                const objToSerialize = createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet);

                const testSerialization = JSON.stringify(objToSerialize, nullReplacer);

                if (testSerialization.length <= MAX_SERIALIZED_SIZE) {
                    return testSerialization;
                }
            }
        }

        // Then, try truncating arrays progressively
        for (const maxLength of PROGRESSIVE_ARRAY_LIMITS) {
            for (const pathArray of sortedArrayPaths) {
                const originalSize = arrayMap.get(pathArray);

                if (originalSize <= maxLength) {
                    continue;
                }

                const didTruncate = truncateArrayAtPath(workingObj, pathArray, maxLength);

                if (didTruncate) {
                    truncatedFieldsSet.add(pathArrayToString(pathArray));

                    const objToSerialize = createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet);

                    const testSerialization = JSON.stringify(objToSerialize, nullReplacer);

                    if (testSerialization.length <= MAX_SERIALIZED_SIZE) {
                        return testSerialization;
                    }
                }
            }
        }

        // Finally, remove object properties if necessary
        const hasTruncatedFields = truncatedFieldsSet.size > 0;
        const currentSize = hasTruncatedFields
            ? JSON.stringify(createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet), nullReplacer).length
            : JSON.stringify(workingObj, nullReplacer).length;

        if (currentSize > MAX_SERIALIZED_SIZE) {
            const didSucceed = removeObjectProperties(workingObj, MAX_SERIALIZED_SIZE, truncatedFieldsSet);
            if (didSucceed) {
                return JSON.stringify(createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet), nullReplacer);
            }
        }

        // In case we truncate, but it still wasn't enough to be under the limit
        const finalObj =
            truncatedFieldsSet.size > 0
                ? createObjectWithTruncationMetadata(workingObj, truncatedFieldsSet)
                : workingObj;

        return JSON.stringify(finalObj, nullReplacer);
    } catch (e) {
        return e.message;
    }
};

export const serializeAppEvent = event => {
    const serializedEvent = { ...event };

    serializedEvent.value = serializedField(event.value);
    serializedEvent.prevValue = serializedField(event.prevValue);

    if (typeof event.data === 'string') {
        serializedEvent.data = { rawData: JSON.stringify({ stringContent: event.data }) };
    } else if (isNil(event.data)) {
        serializedEvent.data = { rawData: JSON.stringify({}) };
    } else {
        const rawDataResult = serializedField(event.data);
        serializedEvent.data = {
            rawData: rawDataResult,
            ...event.data,
        };
    }

    return serializedEvent;
};
